import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useClientCardController } from '@/controllers/clients/clientCard';
import { useAccountController } from '@/controllers/account/useAccountController';
import type { AccountSearch } from '@/models/clients/types';
import { currencySign } from '@/lib/currency';
import { countryStore } from '@/stores/country.store';
import { ClientHeader } from './ClientHeader';
import { ClientSummary } from './ClientSummary';
import { GeneralInfoTab } from './tabs/GeneralInfoTab';
import { OrganizationDetailsTab } from './tabs/OrganizationDetailsTab';
import { ServicesTab } from './tabs/ServicesTab';
import { BalanceDetailTab } from './tabs/BalanceDetailTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { OwnerTransferModal } from './modals/OwnerTransferModal';
import { TariffChangeModal } from './modals/TariffChangeModal';
import { DiscountModal } from './modals/DiscountModal';
import { StatusModal } from './modals/StatusModal';
import { HistoryModal } from './modals/HistoryModal';

const TABS = [
  { label: 'Общая информация', value: 0 },
  { label: 'Услуги', value: 2 },
  { label: 'Финансы', value: 3 },
  { label: 'Документы', value: 4 },
];

export const ClientCard = observer(function ClientCard() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation() as {
    state: { idAccount: string; accountSearch: AccountSearch } | null;
  };
  const idAccount = state?.idAccount ?? id;

  const ctrl = useClientCardController(idAccount);
  const account = useAccountController(idAccount);

  const accountSearch = state?.accountSearch;
  const clientBalance = parseFloat(accountSearch?.balance ?? '0') || 0;
  // Страна приходит в строке поиска, а в новой вкладке или по прямой ссылке её нет —
  // тогда определяем по префиксу логина.
  const currency = currencySign(
    countryStore.byName.get(accountSearch?.country_name ?? '') ??
      countryStore.byLogin(accountSearch?.login ?? account.data?.login)
  );

  return (
    <div className='min-h-screen bg-background p-6 space-y-5'>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <ClientHeader onHome={() => navigate('/clients')} onBack={() => navigate(-1)} />
        <ClientSummary
          account={account.data}
          accountSearch={accountSearch}
          clientBalance={clientBalance}
          currency={currency}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.35 }}
        className='border border-border/30 rounded-2xl bg-card overflow-hidden'
      >
        <div className='flex border-b border-border/30 overflow-x-auto'>
          {TABS.map((tab) => (
            <Button
              key={tab.value}
              variant='ghost'
              onClick={() => ctrl.tabs.setActiveTab(tab.value)}
              className={cn(
                'px-5 py-3.5 text-sm font-medium whitespace-nowrap relative h-auto rounded-none',
                ctrl.tabs.activeTab === tab.value
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
              {ctrl.tabs.activeTab === tab.value && (
                <motion.div
                  layoutId='tab-indicator'
                  className='absolute bottom-0 left-0 right-0 h-0.5 bg-primary'
                />
              )}
            </Button>
          ))}
        </div>

        <AnimatePresence mode='wait'>
          <motion.div
            key={ctrl.tabs.activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className='p-5'
          >
            {ctrl.tabs.activeTab === 0 && (
              <GeneralInfoTab
                account={account.data}
                accountSearch={accountSearch}
                isLoading={account.isLoading}
                error={account.error}
              />
            )}
            {ctrl.tabs.activeTab === 1 && <OrganizationDetailsTab />}
            {ctrl.tabs.activeTab === 2 && (
              <ServicesTab
                services={ctrl.services.items}
                currency={currency}
                isLoading={ctrl.services.isLoading}
                error={ctrl.services.error}
                onOpenTariff={(idCamera, cameraName, currentTariffName) =>
                  ctrl.tariffModal.open(
                    idCamera,
                    cameraName,
                    currentTariffName,
                    accountSearch?.account_type_name,
                    accountSearch?.country_name
                  )
                }
                onOpenDiscount={ctrl.discountModal.open}
                onOpenStatus={ctrl.statusModal.open}
                onOpenOwner={ctrl.ownerModal.open}
                onOpenHistory={ctrl.historyModal.open}
              />
            )}
            {ctrl.tabs.activeTab === 3 && (
              <BalanceDetailTab
                items={ctrl.balance.items}
                currency={currency}
                isLoading={ctrl.balance.isLoading}
                error={ctrl.balance.error}
                dateFilter={ctrl.balance.dateFilter}
                expandedRows={ctrl.balance.expandedRows}
                onDateFilterChange={ctrl.balance.setDateFilter}
                onToggleExpanded={ctrl.balance.toggleExpanded}
              />
            )}
            {ctrl.tabs.activeTab === 4 && <DocumentsTab />}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <OwnerTransferModal ctrl={ctrl.ownerModal} />
      <TariffChangeModal ctrl={ctrl.tariffModal} />
      <DiscountModal ctrl={ctrl.discountModal} />
      <StatusModal ctrl={ctrl.statusModal} />
      <HistoryModal ctrl={ctrl.historyModal} />
    </div>
  );
});
